import {
  Avatar,
  AvatarFallback,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  cn,
} from "@getgreenline/blaze-ui";
import {
  ArrowLeft,
  Building2,
  ChartNoAxesCombined,
  FolderKanban,
  LayoutDashboard,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useBrandsCatalog } from "@/hooks/use-brands-catalog";
import { useAuthStore } from "../../stores/auth-store";
import { BlazeDarkLogo, BlazeLightLogo } from "../icons/blaze-logo";
import { getInitials } from "@/utils/strings";

type AppSidebarNavProps = {
  isOpen: boolean;
  variant?: "platform" | "backoffice";
};

type NavItemProps = {
  label: string;
  to: string;
  activePathPrefix: string;
  Icon: LucideIcon;
  isOpen: boolean;
  currentPath: string;
  exact?: boolean;
};

const platformNavItems = [
  {
    label: "Campaigns",
    to: "/campaigns",
    activePathPrefix: "/campaigns",
    Icon: FolderKanban,
  },
  {
    label: "Analytics",
    to: "/analytics",
    activePathPrefix: "/analytics",
    Icon: LayoutDashboard,
  },
  {
    label: "Brand Performance",
    to: "/brand-performance",
    activePathPrefix: "/brand-performance",
    Icon: ChartNoAxesCombined,
  },
];

const backofficeNavItems = [
  {
    label: "Brands",
    to: "/backoffice",
    activePathPrefix: "/backoffice",
    Icon: Building2,
    exact: true,
  },
  {
    label: "Users",
    to: "/backoffice/users",
    activePathPrefix: "/backoffice/users",
    Icon: UsersRound,
  },
];

const backToAdsNavItem = {
  label: "Back to Blaze Ads",
  to: "/campaigns",
  activePathPrefix: "/campaigns",
  Icon: ArrowLeft,
};

export function AppSidebarNav({
  isOpen,
  variant = "platform",
}: AppSidebarNavProps) {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const realm = useAuthStore((s) => s.realm);
  const accessibleBrandsQuery = useBrandsCatalog("", {
    enabled: realm !== "staff",
  });
  const canAccessBrandPerformance =
    realm === "staff" || Boolean(accessibleBrandsQuery.data?.length);
  const navItems =
    variant === "backoffice"
      ? backofficeNavItems
      : platformNavItems.filter(
          (item) =>
            item.to !== "/brand-performance" || canAccessBrandPerformance,
        );
  const groupLabel = variant === "backoffice" ? "Backoffice" : "Platform";

  const userAvatar = (
    <Avatar className="tw:h-8 tw:w-8 tw:shrink-0">
      <AvatarFallback className="tw:text-xs tw:font-medium">
        {getInitials(user?.name ?? "BLAZE Ads")}
      </AvatarFallback>
    </Avatar>
  );

  return (
    <aside
      className={cn(
        "tw:sticky tw:top-0 tw:h-screen tw:self-start tw:shrink-0 tw:overflow-hidden tw:border-r tw:transition-[width] tw:duration-200 tw:ease-linear tw:bg-[#F7F8F8] tw:dark:bg-[#0D162E]",
        isOpen ? "tw:w-64" : "tw:w-12",
      )}
    >
      <Sidebar
        collapsible="none"
        className="tw:h-full tw:w-full tw:border-0 tw:bg-transparent"
      >
        <SidebarHeader className="tw:h-12 tw:p-0">
          <div
            className={cn(
              "tw:h-12 tw:justify-center tw:w-full tw:flex tw:items-center tw:gap-3",
            )}
          >
            <div
              className={cn("tw:block tw:dark:hidden", isOpen ? "" : "tw:w-6")}
            >
              <BlazeLightLogo collapsed={!isOpen} />
            </div>

            <div
              className={cn("tw:hidden tw:dark:block", isOpen ? "" : "tw:w-6")}
            >
              <BlazeDarkLogo collapsed={!isOpen} />
            </div>
          </div>
        </SidebarHeader>

        <SidebarSeparator className={"tw:w-full tw:m-0 tw:border-b"} />

        <SidebarContent>
          {variant === "backoffice" ? (
            <SidebarGroup className="tw:pb-0">
              <SidebarGroupContent>
                <SidebarMenu>
                  <NavItem
                    {...backToAdsNavItem}
                    currentPath={location.pathname}
                    isOpen={isOpen}
                  />
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ) : null}
          <SidebarGroup>
            {isOpen ? (
              <SidebarGroupLabel className="tw:uppercase">
                {groupLabel}
              </SidebarGroupLabel>
            ) : null}
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <NavItem
                    key={item.to}
                    {...item}
                    currentPath={location.pathname}
                    isOpen={isOpen}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarSeparator className={"tw:w-full tw:m-0 tw:border-b"} />

        <SidebarFooter className="tw:p-2">
          {isOpen ? (
            <div className="tw:flex tw:gap-2 tw:items-center tw:p-2">
              {userAvatar}
              <div>
                <p className="tw:mt-1 tw:text-sm tw:font-semibold tw:text-foreground tw:text-nowrap">
                  {user?.name ?? "Blaze Ads"}
                </p>
                <p className="tw:text-xs tw:text-muted-foreground tw:text-nowrap">
                  {realm === "staff" ? "Staff" : "Brand account"}
                </p>
              </div>
            </div>
          ) : (
            <div className="tw:flex tw:shrink-0 tw:items-center">
              {userAvatar}
            </div>
          )}
        </SidebarFooter>
      </Sidebar>
    </aside>
  );
}

function NavItem({
  label,
  to,
  activePathPrefix,
  Icon,
  isOpen,
  currentPath,
  exact = false,
}: NavItemProps) {
  const isActive = exact
    ? currentPath === activePathPrefix
    : currentPath.startsWith(activePathPrefix);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive}>
        <NavLink
          to={to}
          className={cn(
            "tw:font-medium",
            isActive
              ? "tw:text-sidebar-accent-foreground tw:bg-sidebar-accent"
              : "",
          )}
        >
          <Icon className="tw:h-4 tw:w-4 tw:shrink-0" />
          {isOpen ? <span>{label}</span> : null}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
